import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';

const TABS = ['AGM Meetings', 'My Attendance', 'Hotel Booking'];

export default function MemberAGM() {
  const [tab, setTab] = useState(0);
  const [meetings, setMeetings] = useState([]);
  const [attendances, setAttendances] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [regLoading, setRegLoading] = useState(null);
  const [search, setSearch] = useState('');
  const [showRegModal, setShowRegModal] = useState(null); // holds agm id
  const [regForm, setRegForm] = useState({ payment_date: '', amount_paid: '', receipt: null });

  // Hotel form
  const [hotelForm, setHotelForm] = useState({ agm: '', hotel_name: '', room_type: 'single', check_in: '', check_out: '', nights: 1, special_requests: '' });
  const [hotelSubmitting, setHotelSubmitting] = useState(false);

  const fetchMeetings = useCallback(() => {
    setLoading(true);
    api.get('/agm/meetings/')
      .then(res => setMeetings(res.data.results || res.data))
      .catch(() => toast.error('Failed to load AGM meetings'))
      .finally(() => setLoading(false));
  }, []);

  const fetchAttendances = useCallback(() => {
    setLoading(true);
    api.get('/agm/my-attendances/')
      .then(res => setAttendances(res.data.results || res.data))
      .catch(() => toast.error('Failed to load attendances'))
      .finally(() => setLoading(false));
  }, []);

  const fetchHotels = useCallback(() => {
    setLoading(true);
    api.get('/agm/my-hotels/')
      .then(res => setHotels(res.data.results || res.data))
      .catch(() => toast.error('Failed to load hotel bookings'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (tab === 0) { fetchMeetings(); fetchAttendances(); }
    else if (tab === 1) fetchAttendances();
    else if (tab === 2) { fetchHotels(); fetchMeetings(); }
  }, [tab, fetchMeetings, fetchAttendances, fetchHotels]);

  const attendanceByAgm = {};
  attendances.forEach(a => { attendanceByAgm[a.agm] = a.status || 'pending'; });

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!showRegModal) return;
    setRegLoading(showRegModal);
    try {
      const formData = new FormData();
      if (regForm.payment_date) formData.append('payment_date', regForm.payment_date);
      if (regForm.amount_paid) formData.append('amount_paid', regForm.amount_paid);
      if (regForm.receipt) formData.append('receipt', regForm.receipt);
      await api.post(`/agm/meetings/${showRegModal}/register/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Registered successfully!');
      setShowRegModal(null);
      setRegForm({ payment_date: '', amount_paid: '', receipt: null });
      fetchMeetings();
      fetchAttendances();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setRegLoading(null);
    }
  };

  const handleHotelSubmit = async (e) => {
    e.preventDefault();
    if (!hotelForm.agm) { toast.error('Please select an AGM'); return; }
    setHotelSubmitting(true);
    try {
      await api.post(`/agm/meetings/${hotelForm.agm}/hotel/`, {
        hotel_name: hotelForm.hotel_name,
        room_type: hotelForm.room_type,
        check_in: hotelForm.check_in,
        check_out: hotelForm.check_out,
        nights: hotelForm.nights,
        special_requests: hotelForm.special_requests,
      });
      toast.success('Hotel booking saved!');
      setHotelForm({ agm: '', hotel_name: '', room_type: 'single', check_in: '', check_out: '', nights: 1, special_requests: '' });
      fetchHotels();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to book hotel');
    } finally {
      setHotelSubmitting(false);
    }
  };

  const StatusBadge = ({ value, trueLabel, falseLabel, trueColor = 'green', falseColor = 'gray' }) => (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${value ? `bg-${trueColor}-100 text-${trueColor}-700` : `bg-${falseColor}-100 text-${falseColor}-500`}`}>
      {value ? trueLabel : falseLabel}
    </span>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">AGM</h1>
      </div>

      {/* Search */}
      <div className="relative">
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
        <input type="text" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#0B5D1E] focus:border-transparent" />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
        {TABS.map((t, i) => (
          <button key={t} onClick={() => setTab(i)}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition ${tab === i ? 'bg-white text-[#0B5D1E] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {t}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-[#0B5D1E] border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-400">Loading...</p>
        </div>
      ) : (
        <>
          {/* TAB 0: AGM Meetings */}
          {tab === 0 && (
            meetings.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
                <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <h2 className="text-lg font-semibold text-gray-500 mb-2">No AGM Meetings</h2>
                <p className="text-gray-400">There are no AGM meetings available at the moment.</p>
              </div>
            ) : (
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {meetings.filter(m => !search || m.title.toLowerCase().includes(search.toLowerCase()) || (m.venue || '').toLowerCase().includes(search.toLowerCase()) || String(m.year).includes(search)).map((m, idx) => (
                  <div key={m.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition relative">
                    <div className="absolute top-2 left-2 bg-[#0B5D1E] text-white text-[10px] font-bold w-6 h-6 rounded-full flex items-center justify-center z-10">{idx + 1}</div>
                    {m.cover_image && (
                      <div className="h-40 bg-gray-50">
                        <img src={m.cover_image} alt={m.title} className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="p-4 space-y-2">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-gray-800 truncate">{m.title}</h3>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${m.status === 'upcoming' ? 'bg-blue-100 text-blue-700' : m.status === 'ongoing' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                          {m.status_display || m.status}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 space-y-1">
                        <p><span className="font-medium text-gray-600">Year:</span> {m.year}</p>
                        <p><span className="font-medium text-gray-600">Date:</span> {m.date}</p>
                        {m.venue && <p><span className="font-medium text-gray-600">Venue:</span> {m.venue}</p>}
                        <p><span className="font-medium text-gray-600">Registered:</span> {m.registered_count}</p>
                      </div>
                      {m.status !== 'completed' && attendanceByAgm[m.id] === 'approved' && (
                        <div className="w-full mt-3 flex items-center justify-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-lg text-sm font-semibold">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                          Already Registered
                        </div>
                      )}
                      {m.status !== 'completed' && (attendanceByAgm[m.id] === 'pending' || attendanceByAgm[m.id] === 'rejected') && (
                        <div className="w-full mt-3 flex items-center justify-center gap-2 px-4 py-2 bg-yellow-50 text-yellow-700 rounded-lg text-sm font-semibold">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                          Pending Approval
                        </div>
                      )}
                      {m.status !== 'completed' && !attendanceByAgm[m.id] && (
                        <button onClick={() => { setShowRegModal(m.id); setRegForm({ payment_date: '', amount_paid: '', receipt: null }); }}
                          className="w-full mt-3 flex items-center justify-center gap-2 px-4 py-2 bg-[#0B5D1E] text-white rounded-lg text-sm font-semibold hover:bg-[#094a18] transition">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                          </svg>
                          Register
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

          {/* TAB 1: My Attendance */}
          {tab === 1 && (
            attendances.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
                <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <h2 className="text-lg font-semibold text-gray-500 mb-2">No AGM Registrations</h2>
                <p className="text-gray-400">You haven't registered for any AGM yet.</p>
              </div>
            ) : (
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {attendances.filter(a => !search || a.agm_title.toLowerCase().includes(search.toLowerCase())).map((a, idx) => (
                  <div key={a.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 space-y-3 hover:shadow-md transition">
                    <div className="flex items-center gap-2">
                      <span className="bg-[#0B5D1E] text-white text-[10px] font-bold w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0">{idx + 1}</span>
                      <h3 className="font-semibold text-gray-800">{a.agm_title}</h3>
                    </div>
                    <div className="text-xs text-gray-500">
                      <p>Registered: {new Date(a.registered_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <StatusBadge value={a.attendance_marked} trueLabel="Present" falseLabel="Not Marked" />
                      <StatusBadge value={a.materials_collected} trueLabel="Bag Collected" falseLabel="Bag Not Collected" trueColor="green" falseColor="yellow" />
                      <StatusBadge value={a.certificate_issued} trueLabel="Certificate Issued" falseLabel="No Certificate" trueColor="green" falseColor="gray" />
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

          {/* TAB 2: Hotel Booking */}
          {tab === 2 && (
            <div className="space-y-6">
              {/* Hotel Form */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Book Hotel Accommodation</h2>
                <form onSubmit={handleHotelSubmit} className="grid gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Select AGM *</label>
                    <select value={hotelForm.agm} onChange={e => setHotelForm({ ...hotelForm, agm: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0B5D1E] focus:border-transparent" required>
                      <option value="">-- Choose AGM --</option>
                      {meetings.filter(m => m.status !== 'completed').map(m => (
                        <option key={m.id} value={m.id}>{m.title} ({m.year})</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Hotel Name *</label>
                    <input type="text" value={hotelForm.hotel_name} onChange={e => setHotelForm({ ...hotelForm, hotel_name: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0B5D1E] focus:border-transparent" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Room Type</label>
                    <select value={hotelForm.room_type} onChange={e => setHotelForm({ ...hotelForm, room_type: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0B5D1E] focus:border-transparent">
                      <option value="single">Single</option>
                      <option value="double">Double</option>
                      <option value="suite">Suite</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Check-in Date *</label>
                    <input type="date" value={hotelForm.check_in} onChange={e => setHotelForm({ ...hotelForm, check_in: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0B5D1E] focus:border-transparent" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Check-out Date *</label>
                    <input type="date" value={hotelForm.check_out} onChange={e => setHotelForm({ ...hotelForm, check_out: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0B5D1E] focus:border-transparent" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Number of Nights</label>
                    <input type="number" min="1" value={hotelForm.nights} onChange={e => setHotelForm({ ...hotelForm, nights: parseInt(e.target.value) || 1 })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0B5D1E] focus:border-transparent" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Special Requests</label>
                    <textarea value={hotelForm.special_requests} onChange={e => setHotelForm({ ...hotelForm, special_requests: e.target.value })}
                      rows={3} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0B5D1E] focus:border-transparent" />
                  </div>
                  <div className="sm:col-span-2">
                    <button type="submit" disabled={hotelSubmitting}
                      className="px-6 py-2 bg-[#0B5D1E] text-white rounded-lg text-sm font-semibold hover:bg-[#094a18] transition disabled:opacity-50">
                      {hotelSubmitting ? 'Saving...' : 'Submit Hotel Booking'}
                    </button>
                  </div>
                </form>
              </div>

              {/* My Hotel Bookings */}
              {hotels.length > 0 && (
                <div>
                  <h2 className="text-lg font-semibold text-gray-800 mb-4">My Hotel Bookings</h2>
                  <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                    {hotels.filter(h => !search || h.hotel_name.toLowerCase().includes(search.toLowerCase()) || (h.agm_title || '').toLowerCase().includes(search.toLowerCase())).map((h, idx) => (
                      <div key={h.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 space-y-2 hover:shadow-md transition">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="bg-[#0B5D1E] text-white text-[10px] font-bold w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0">{idx + 1}</span>
                            <h3 className="font-semibold text-gray-800">{h.hotel_name}</h3>
                          </div>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${h.confirmed ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                            {h.confirmed ? 'Confirmed' : 'Pending'}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 space-y-1">
                          <p><span className="font-medium text-gray-600">AGM:</span> {h.agm_title}</p>
                          <p><span className="font-medium text-gray-600">Room:</span> {h.room_type_display || h.room_type}</p>
                          <p><span className="font-medium text-gray-600">Check-in:</span> {h.check_in}</p>
                          <p><span className="font-medium text-gray-600">Check-out:</span> {h.check_out}</p>
                          <p><span className="font-medium text-gray-600">Nights:</span> {h.nights}</p>
                          {h.special_requests && <p><span className="font-medium text-gray-600">Requests:</span> {h.special_requests}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Registration Modal */}
      {showRegModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowRegModal(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-800">AGM Registration</h2>
              <button onClick={() => setShowRegModal(null)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <form onSubmit={handleRegister} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date of Payment *</label>
                <input type="date" value={regForm.payment_date} onChange={e => setRegForm({ ...regForm, payment_date: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0B5D1E] focus:border-transparent" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount Paid (₦) *</label>
                <input type="number" step="0.01" min="0" value={regForm.amount_paid} onChange={e => setRegForm({ ...regForm, amount_paid: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0B5D1E] focus:border-transparent" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Upload Receipt *</label>
                <input type="file" accept="image/*,.pdf" onChange={e => {
                  const file = e.target.files[0] || null;
                  if (file && file.size > 100 * 1024) {
                    toast.error('Receipt file must be less than 100KB');
                    e.target.value = '';
                    return;
                  }
                  setRegForm({ ...regForm, receipt: file });
                }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm file:mr-3 file:py-1 file:px-3 file:rounded-md file:border-0 file:bg-[#0B5D1E]/10 file:text-[#0B5D1E] file:text-sm file:font-semibold" required />
                <p className="text-xs text-gray-400 mt-1">Max file size: 100KB</p>
              </div>
              <button type="submit" disabled={regLoading}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#0B5D1E] text-white rounded-lg text-sm font-semibold hover:bg-[#094a18] transition disabled:opacity-50">
                {regLoading ? (
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                ) : 'Submit Registration'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
